import React, { useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  Badge,
  Box,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AlertCriticalIcon,
  ExpandLessIcon,
  LaunchIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { HOVER_DELAY, prorateCalculator } from './constants';
import { keyframes } from '@emotion/react';

// Define the fill animation keyframes
const fillAnimation = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

// Define the unfill animation keyframes
const unfillAnimation = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0;
  }
`;

const BaseNavListItem = ({
  value,
  icon,
  open,
  onClick,
  setOpen,
  children,
  isSubOption,
  notification,
  text,
  onMouseEnter,
  onMouseLeave,
  selected,
  href,
  sx,
  to,
  target,
  isTransitioning,
  flags,
  showWarningIcon = false,
}) => {
  // State to track hover status
  const [isHovered, setIsHovered] = useState(false);
  const useUxImprovements = flags && flags.navigationMenuUxImprovements;

  // Helper function to render warning icon
  const renderWarningIcon = () =>
    showWarningIcon ? (
      <AlertCriticalIcon
        sx={{
          fontSize: '20px',
          color: 'warning.dark',
        }}
      />
    ) : null;

  // Custom handlers that update hover state and call the passed handlers
  const handleMouseEnter = (e) => {
    setIsHovered(true);
    if (onMouseEnter && !isTransitioning) {
      onMouseEnter(e);
    }
  };

  const handleMouseLeave = (e) => {
    setIsHovered(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };
  const ConditionalBadge = isSubOption && notification ? Badge : React.Fragment;
  const badgeColor = {
    // `secondary` is purple in the theme.
    'needs-help': 'secondary',
    new: 'error',
    unresolved: 'primary',
  }[notification];
  const ConditionalBadgeProps =
    isSubOption && notification
      ? {
          variant: 'dot',
          color: badgeColor,
          sx: { width: '100%' },
          slotProps: {
            badge: {
              style: {
                top: 8,
                right: 8,
              },
            },
          },
        }
      : {};

  const Wrapper = to ? ReactRouterLink : React.Fragment;
  const WrapperProps = to
    ? { to, style: { textDecoration: 'none', color: 'unset' } }
    : {};
  const isProrateCalculator = value === prorateCalculator;

  return (
    <Wrapper {...WrapperProps}>
      <Box
        className={!isSubOption ? 'nav-list-item' : undefined}
        onMouseEnter={isTransitioning ? undefined : handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isProrateCalculator ? <Divider sx={{ marginY: 2 }} /> : null}
        <ListItemButton
          component={href ? 'a' : undefined}
          id={value}
          onClick={onClick ? onClick : () => setOpen(!open)}
          sx={{
            ...sx,
            color: 'unset !important',
            ...(!isSubOption &&
              useUxImprovements && {
                transitionDelay: `${HOVER_DELAY}ms`,
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  height: '100%',
                  width: isHovered ? '100%' : '0%',
                  backgroundColor: 'action.hover',
                  zIndex: -1,
                  animation: isHovered
                    ? `${fillAnimation} ${HOVER_DELAY}ms ease-in-out forwards`
                    : `${unfillAnimation} ${HOVER_DELAY}ms ease-in-out forwards`,
                },
              }),
            ...(!isSubOption &&
              !useUxImprovements && {
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }),
          }}
          selected={selected}
          target={target}
          href={href}
        >
          <ConditionalBadge {...ConditionalBadgeProps}>
            {icon ? (
              <ListItemIcon
                sx={{
                  pointerEvents: 'none',
                  minWidth: 'unset',
                  marginRight: 1,
                }}
              >
                {!isSubOption && notification ? (
                  <Badge variant="dot" color={badgeColor}>
                    {icon}
                  </Badge>
                ) : (
                  icon
                )}
              </ListItemIcon>
            ) : null}
            <ListItemText
              sx={{
                pointerEvents: 'none',
              }}
              primary={
                !isSubOption ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="labelLarge">{text}</Typography>
                    {renderWarningIcon()}
                  </Box>
                ) : null
              }
              primaryTypographyProps={{
                lineHeight: isProrateCalculator ? '1 !important' : '26px',
              }}
              secondary={
                isSubOption ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="bodySmall">{text}</Typography>
                    {renderWarningIcon()}
                  </Box>
                ) : null
              }
            />

            {children ? (
              <ListItemSecondaryAction
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <ExpandLessIcon
                  sx={{
                    transition: ({ transitions: { create } }) =>
                      create('transform'),
                    transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}
                />
              </ListItemSecondaryAction>
            ) : href ? (
              <ListItemSecondaryAction
                sx={{
                  height: 17.5,
                  top: 'calc(50% - 8.75px)',
                  transform: 'unset',
                }}
              >
                <LaunchIcon fontSize="16px" color="action" />
              </ListItemSecondaryAction>
            ) : null}
          </ConditionalBadge>
        </ListItemButton>
        {children}
      </Box>
    </Wrapper>
  );
};

export default BaseNavListItem;
